import type { TimelineStage } from '../types/chatbot-message.types';
import {
  CheckpointStatus,
  ExperimentStatus,
  type Experiment,
  type ExperimentKpis,
  type TimelineCheckpoint,
} from '../types/experiment.types';

interface DeriveStageInput {
  experiment?: Experiment | undefined;
  kpis?: ExperimentKpis | undefined;
  timeline?: TimelineCheckpoint[] | undefined;
}

/**
 * Derive a chatbot timeline stage from real experiment state.
 *
 * The current backend status enum (`DRAFT | CREATED | RUNNING | COMPLETED | CANCELLED | REJECTED`)
 * doesn't expose checkpoint sub-states (e.g. "Day 7 input pending"), so we
 * approximate via `daysElapsed` buckets + the first `in_progress` checkpoint.
 *
 * TODO Phase 4a — once `agentic-integrations` ships the `ExperimentLifecycleWorkflow`
 * with explicit checkpoint states, replace this approximation with a direct read.
 */
export function deriveTimelineStage({
  experiment,
  kpis,
  timeline,
}: DeriveStageInput): TimelineStage {
  if (!experiment) return 'day-0';

  if (experiment.status === ExperimentStatus.COMPLETED) return 'day-28-7';

  if (
    experiment.status === ExperimentStatus.DRAFT ||
    experiment.status === ExperimentStatus.CREATED
  ) {
    return 'day-0';
  }

  // For CANCELLED or REJECTED, freeze at the last passed checkpoint's day bucket.
  // REJECTED = operator declined the approval gate (BUG-029); the panel appends
  // an appropriate terminal narrative on top.
  const isTerminalEarly =
    experiment.status === ExperimentStatus.CANCELLED ||
    experiment.status === ExperimentStatus.REJECTED;
  const days = isTerminalEarly
    ? (lastPassedDay(timeline) ?? kpis?.daysElapsed ?? experiment.progressDays)
    : (kpis?.daysElapsed ?? experiment.progressDays);

  if (days >= 35) return 'day-28-7';
  if (days >= 28) return 'day-28';
  if (days >= 17) return 'day-17';
  if (days >= 7) {
    // Day 7 is the only checkpoint that can sit in `in_progress` waiting for
    // user input. If a Day-7 checkpoint is in progress on the timeline, the
    // user hasn't proceeded yet; otherwise they have.
    return isDay7InProgress(timeline) ? 'day-7' : 'day-7-proceeded';
  }
  if (days >= 3) return 'day-3';
  if (days >= 2) return 'day-2';
  if (days >= 1) return 'day-1';
  return 'day-0';
}

/** Heuristic: true when a checkpoint whose label starts with "Day 7" is `in_progress`. */
function isDay7InProgress(timeline: TimelineCheckpoint[] | undefined): boolean {
  if (!timeline) return false;
  return timeline.some(
    (cp) => cp.status === CheckpointStatus.IN_PROGRESS && /^day\s*7\b/i.test(cp.dayLabel),
  );
}

/**
 * Best-effort day count for the most recently passed checkpoint, parsed from
 * `dayLabel` strings like `"Day 0"`, `"Day 1–2"`, `"Day 28+7"`. Returns the
 * first numeric run found; `undefined` if no passed checkpoint exists.
 */
function lastPassedDay(timeline: TimelineCheckpoint[] | undefined): number | undefined {
  if (!timeline) return undefined;
  for (let i = timeline.length - 1; i >= 0; i -= 1) {
    const cp = timeline[i];
    if (cp?.status === CheckpointStatus.PASSED) {
      const match = /(\d+)/.exec(cp.dayLabel);
      if (match) return Number(match[1]);
    }
  }
  return undefined;
}
